import React, {Component} from 'react';
import PropTypes from 'prop-types'
import { Route, Redirect, Switch } from 'react-router-dom'
import qs from 'qs';

import Report from './Report'
import Estimate from './Estimate'
import Summary from './Summary'
import Modernize from './Modernize'
import URLpage from './URLpage'
import SideBar from '../components/SideBar'
import MyNavBar from '../components/MyNavBar'
import { calculateTotalComplexity, combineCompliance, convertToNewFormat, flattenHeirarchy, addNameToAssets, getDateFromUrl } from '../utils/data'

import sample_json from "../resources/sample.json";
import sample_data from "../resources/sample.csv";
import sample_compliance from "../resources/sample_compliance.csv";
import sample_stageinventory from "../resources/sample_stageinventory.csv";

import '../css/index.css';
import '../css/dependencies.css';
import '../css/style.css';

import getCSV from '../utils/getCSV';
import getJSON from '../utils/getJSON';
import decodeDataUrl from '../utils/decodeDataUrl';

// List of email domains that have access to estimate page
const enableEstimateWhiteList = [
    "datamigrators.com",
    "mettleci.com",
    "ibm.com"
];

class App extends Component {

    static propTypes = {
        match: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired
    }

    constructor(props){
        super(props);

        this.state = {
            data : undefined,
            loading : false,
            fileName : "",
            clientName : "",
            enableEstimate : false
        }
    }

    componentDidMount() {
        const query = qs.parse(this.props.location.search.replace("?",""));
        let dataUrl = query.data;

        if (dataUrl) {
            dataUrl = decodeDataUrl(dataUrl);
            this.loadJsonData(dataUrl);
        }
    }

    componentDidUpdate(prevProps) {
        let dataUrl = qs.parse(this.props.location.search.replace("?","")).data;
        let prevDataUrl = qs.parse(prevProps.location.search.replace("?","")).data;

        if (dataUrl !== prevDataUrl) {
            dataUrl = decodeDataUrl(dataUrl);
            this.loadJsonData(dataUrl);
        }
    }

    shouldDisplayEstimate = () => {
        if (this.state.fileName === "sample" || this.state.fileName === "sample.csv") {
            this.setState({ enableEstimate: true });
            return;
        }

        const emailDomain = this.state.emailAddress.split("@")[1];

        let enableEstimate = false;
        for (let domain of enableEstimateWhiteList) {
            if (emailDomain.includes(domain)) {
                enableEstimate = true;
                break;
            }
        }

        this.setState({ enableEstimate });
    }

    async loadJsonData(url) {
        if(!url) {
            this.setState({
                data: null,
                errorMessage : "URL cannot be empty"
            });
            return;
        }

        this.setState({
            errorMessage : null,
            loading : true
        });

        let dataUrl;
        if (url === "sample") {
            dataUrl = "sample.json";
        } else if (url === "sample2") {
            dataUrl = "sample2.json";
        } else if (url.endsWith('.csv')) {
            dataUrl = `${url.slice(0,-4)}.json`;
        } else if (!url.endsWith('.json')) {
            dataUrl = `${url}.json`;
        } else {
            dataUrl = url;
        }

        let data = null;

        // Retrieve the report JSON file and extract data
        try {
            if (dataUrl === "sample.json" || dataUrl === "sample2.json") {
                data = sample_json;
            } else {
                data = await getJSON(dataUrl);
            }
        } catch(err) {
            try {
                // Check if we tried to load JSON, and try CSV.
                // (check for JSON because we may have already tried CSV)
                if (dataUrl.endsWith('.json')) {
                    dataUrl = `${dataUrl.slice(0,-5)}.csv`;
                    data = await this.loadCsvData(dataUrl);
                }
            } catch(error) {
                this.setState({
                    errorMessage : "Could not load data file",
                    loading: false
                });
                throw error;
            }
        }

        if (Object.keys(data.assets).length > 0) {
            let assets = data.assets;

            if ("reportVersion" in data && data.reportVersion >= 2) {
                assets = flattenHeirarchy(assets);
            } else {
                assets = addNameToAssets(assets);
            }
            assets = calculateTotalComplexity(assets);

            let newState = {
                data: assets,
                hasModernizationInfo: ("reportVersion" in data && data.reportVersion == 3) ? true : false,
                stageInventoryNotAvailable: data.stageInventoryNotAvailable,
                complianceNotAvailable: data.complianceNotAvailable,
                fileName: data.zipfile,
                clientName: data.clientname,
                emailAddress: data.userEmail,
                date: getDateFromUrl(url),
                loading: false
            };

            if ("reportVersion" in data && data.reportVersion >= 3) {
                newState.heirarchyData = data.assets;
            }

            this.setState(newState, this.shouldDisplayEstimate);
        } else {
            this.setState({
                errorMessage : (
                    <span>
                        Uploaded Zip file did not contain Parallel/Server Jobs or Shared Containers.<br/>
                        See our <a href='https://upgrade.mettleci.com/export-isx.html'>guide</a> for creating an ISX Export Zip
                    </span>
                ),
                loading: false
            });
        }

    }

    // If data is in old CSV format, load those files and convert to new format
    async loadCsvData(url) {
        // Get individual URLs for report CSV, compliance CSV and stage inventory CSV
        let reportUrl, complianceUrl, stageInventoryUrl;

        if (url === "sample.csv") {
            reportUrl = sample_data;
            complianceUrl = sample_compliance;
            stageInventoryUrl = sample_stageinventory;
        } else {
            reportUrl = url;
            complianceUrl = `${url.slice(0,-4)}_compliance.csv`;
            stageInventoryUrl = `${url.slice(0,-4)}_stageinventory.csv`;
        }

        let data, compliance, stageInventory;

        // Retrieve the report CSV file and extract data
        try {
            data = await getCSV(reportUrl);
        } catch(err) {
            throw err;
        }

        // Retrieve the compliance and stage inventory CSV file and extract data
        try {
            if (complianceUrl) compliance = await getCSV(complianceUrl);
            if (stageInventoryUrl) stageInventory = await getCSV(stageInventoryUrl);
        } catch(err) {}

        return convertToNewFormat(url, data, compliance, stageInventory);
    }

    render() {
        const { location } = this.props

        let component = undefined;
        if(this.state.data){
            component = (
                <Switch>
                    <Redirect exact from="/" to={{ pathname: "/report", search: location.search }}/>
                    <Route
                        path="/report"
                        render={(props) =>
                            <Report
                                {...props}
                                data={this.state.data}
                                complianceNotAvailable={this.state.complianceNotAvailable}
								fileName={this.state.fileName.slice(0,-4)}
                            />
                        }
                    />
                    { this.state.enableEstimate &&
                        <Route
                            path="/estimate"
                            render={(props) => <Estimate {...props} data={this.state.data}/> }
                        />
                    }
                    <Route
                        path="/summary"
                        render={(props) =>
                            <Summary
                                {...props}
                                data={this.state.data}
								fileName={this.state.fileName.slice(0,-4)}
                                stageInventoryNotAvailable={this.state.stageInventoryNotAvailable}
                                complianceNotAvailable={this.state.complianceNotAvailable}
                            />
                        }
                    />
                    { this.state.hasModernizationInfo &&
                        <Route
                            path="/modernize"
                            render={(props) =>
                                <Modernize
                                    {...props}
                                    data={this.state.heirarchyData}
                                />
                            }
                        />
                    }
                </Switch>
            );
        }else{
            component = (<URLpage loading={this.state.loading} error={this.state.errorMessage}/>);
        }

        return (
            <div className={"sticky-header sidebar-collapsed"}>
                <MyNavBar clientName={this.state.clientName} fileName={this.state.fileName} date={this.state.date}></MyNavBar>
                <SideBar enableEstimate={this.state.enableEstimate} enableModernization={this.state.hasModernizationInfo}></SideBar>
                <div className="body-content">
                    {component}
                    <footer style={{ position : "fixed", height : 40, padding: 12, textAlign : "center"}}>© 2018-2019 Data Migrators</footer>
                </div>
            </div>
        )
    }
}

export default App;
